/**
 * Reset Password page — /admin/reset-password?token=…
 * Consumes the JWT token in the URL and sets a new password.
 */
import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tv, Lock, CheckCircle2, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TIDS = {
  pw1: "reset-password-input",
  pw2: "reset-password-confirm",
  submit: "reset-submit",
  success: "reset-success",
  error: "reset-error",
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw1.length < 3) {
      setError("Password must be at least 3 characters.");
      return;
    }
    if (pw1 !== pw2) {
      setError("The two passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/admin/auth/reset-password`, {
        token,
        new_password: pw1,
      });
      setDone(true);
      // Redirect to login after 3 seconds
      setTimeout(() => navigate("/admin/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900 border-slate-800 p-8 shadow-2xl text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-white mb-2">Missing reset token</h1>
          <p className="text-sm text-slate-400 mb-5">
            This page is only valid when opened from the link in a password-reset email.
          </p>
          <Link to="/admin/forgot-password" className="text-orange-400 hover:text-orange-300 text-sm">
            Request a new reset link
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-900 border-slate-800 p-8 shadow-2xl">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-pink-600 rounded-xl flex items-center justify-center mb-3 shadow-lg shadow-orange-500/20">
            <Tv className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">Choose a new password</h1>
          <p className="text-sm text-slate-400 mt-1 text-center">
            Pick something you&apos;ll remember — you can change it later from Settings.
          </p>
        </div>

        {done ? (
          <div
            data-testid={TIDS.success}
            className="flex items-start gap-2 bg-emerald-950/40 border border-emerald-900/50 text-emerald-300 px-4 py-4 rounded-md text-sm mb-4"
          >
            <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold mb-1">Password updated</div>
              <div className="text-emerald-400/90">Redirecting to the sign-in page…</div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="pw1" className="text-slate-300 text-sm">
                New password
              </Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="pw1"
                  data-testid={TIDS.pw1}
                  type="password"
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  placeholder="At least 3 characters"
                  required
                  autoFocus
                  className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pw2" className="text-slate-300 text-sm">
                Confirm password
              </Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="pw2"
                  data-testid={TIDS.pw2}
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  placeholder="Type it again"
                  required
                  className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid={TIDS.error}
                className="flex items-start gap-2 bg-red-950/40 border border-red-900/50 text-red-300 px-3 py-2.5 rounded-md text-sm"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              data-testid={TIDS.submit}
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 text-white font-semibold py-2.5 disabled:opacity-50"
            >
              {loading ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
